import PropTypes from "prop-types"
import React, { useEffect } from "react"
import { useFormContext } from "react-hook-form"
import {
  BALANCE_NAME_MAPPING,
  INSTALLMENT_SCHEDULE_NAME_MAPPING,
} from "src/main/Billing/constants"

import Form from "src/components/Form"
import Tooltip from "src/components/Tooltip"

const TransientDefaults = ({
  storageProducts,
  reservationInstallmentSchedules,
}) => {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext()

  const BALANCE_COLLECTION_SCHEDULES = ["check_in", "check_out", "monthly"]

  const [defaultLongTermSchedule, defaultShortTermSchedule, schedules] = watch([
    "defaults.long_term_schedule",
    "defaults.short_term_schedule",
    "schedules",
  ])
  const defaultLongTermScheduleEnabled =
    schedules[defaultLongTermSchedule]?.enabled
  const defaultShortTermScheduleEnabled =
    schedules[defaultShortTermSchedule]?.enabled

  useEffect(() => {
    if (!defaultLongTermScheduleEnabled) {
      setValue("defaults.long_term_schedule", "")
    }

    if (!defaultShortTermScheduleEnabled) {
      setValue("defaults.short_term_schedule", "")
    }
  }, [
    defaultLongTermScheduleEnabled,
    defaultShortTermScheduleEnabled,
    setValue,
  ])

  const renderHeadingRow = (title, inputLabel) => {
    return (
      <div className="flex flex-row items-baseline gap-4">
        <div className="flex-3">
          <h4>{title}</h4>
        </div>
        <div className="flex-1 truncate font-bold sm:hidden md:block">
          {inputLabel}
        </div>
        <div className="flex-1" />
      </div>
    )
  }

  const renderTooltip = (text) => {
    return (
      <Tooltip text={text} placement="top" variant="dark" maxWidth="300px">
        <i className="fa-solid fa-circle-exclamation m-1 cursor-pointer text-gray-600" />
      </Tooltip>
    )
  }

  const renderLabel = (id, text, variantText, tooltipText) => {
    return (
      <div className="flex-3">
        <div className="flex truncate">
          <Form.Label htmlFor={id} className="mb-0">
            <span className="font-normal md:pl-4">{text}</span>
            {variantText ? (
              <span className="ml-1 font-bold">{variantText}</span>
            ) : null}
          </Form.Label>
          {tooltipText ? renderTooltip(tooltipText) : null}
        </div>
      </div>
    )
  }

  const renderRevenueCategoryDefaultRow = (productType) => {
    return (
      <div className="flex sm:flex-col sm:gap-2 md:flex-row md:items-baseline md:gap-4">
        {renderLabel(
          `default-product-${productType}`,
          "Default revenue item for",
          productType === "dock" ? "dockage" : "moorage",
          "Marina user can always change this when creating/confirming a reservation"
        )}
        <div className="flex flex-1 flex-wrap">
          <Form.Select
            id={`default-product-${productType}`}
            {...register(`defaults.${productType}_product_id`, {
              required: "A selection is required",
            })}
            hasErrors={Boolean(
              errors?.defaults && errors.defaults[`${productType}_product_id`]
            )}
          >
            <option key="" value="" disabled hidden>
              Please choose ...
            </option>
            {storageProducts.map((product) => {
              return (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              )
            })}
          </Form.Select>
          {errors?.defaults && errors.defaults[`${productType}_product_id`] && (
            <Form.Error>
              {errors.defaults[`${productType}_product_id`].message}
            </Form.Error>
          )}
        </div>
        <div className="flex flex-1"></div>
      </div>
    )
  }

  const renderDepositDefaultRow = ({ fieldId, attributeKey, text }) => {
    const schedules = reservationInstallmentSchedules.filter(
      (schedule) => schedule.schedule !== "monthly"
    )
    return (
      <div className="flex gap-4 sm:flex-col md:flex-row md:items-baseline">
        {renderLabel(
          fieldId,
          "Default billing schedule for reservations",
          text,
          "Marina user can always change this when creating/confirming a reservation"
        )}
        <div className="flex flex-1 flex-wrap">
          <Form.Select
            id={fieldId}
            {...register(`defaults.${attributeKey}`, {
              required: "A selection is required",
            })}
            hasErrors={Boolean(
              errors?.defaults && errors.defaults[`${attributeKey}`]
            )}
          >
            <option key="" value="" disabled>
              Please choose ...
            </option>
            {schedules.map((schedule) => {
              const isEnabled = watch(`schedules.${schedule.schedule}.enabled`)
              return (
                <option
                  key={schedule.schedule}
                  value={schedule.schedule}
                  disabled={!isEnabled}
                >
                  {INSTALLMENT_SCHEDULE_NAME_MAPPING[schedule.schedule]}
                </option>
              )
            })}
          </Form.Select>
          {errors?.defaults && errors.defaults[`${attributeKey}`] && (
            <Form.Error>
              {errors.defaults[`${attributeKey}`].message}
            </Form.Error>
          )}
        </div>
        <div className="flex-1" />
      </div>
    )
  }

  const renderBalanceCollectionDefaultRow = ({
    fieldId,
    attributeKey,
    text,
  }) => {
    return (
      <div className="flex sm:flex-col sm:gap-2 md:flex-row md:items-baseline md:gap-4">
        {renderLabel(
          fieldId,
          "Default balance collection schedule for reservations",
          text,
          "Reservation duration will determine which applies."
        )}
        <div className="flex flex-1 flex-wrap">
          <Form.Select
            id={fieldId}
            {...register(`defaults.${attributeKey}`, {
              required: "A selection is required",
            })}
            hasErrors={Boolean(
              errors?.defaults && errors.defaults[`${attributeKey}`]
            )}
          >
            <option key="" value="" disabled>
              Please choose ...
            </option>
            {BALANCE_COLLECTION_SCHEDULES.map((schedule) => {
              return (
                <option
                  key={schedule}
                  value={schedule}
                  disabled={
                    schedule === "monthly" &&
                    attributeKey === "short_term_balance_collection_schedule"
                  }
                >
                  {BALANCE_NAME_MAPPING[schedule]}
                </option>
              )
            })}
          </Form.Select>
          {errors?.defaults && errors.defaults[`${attributeKey}`] && (
            <Form.Error>
              {errors.defaults[`${attributeKey}`].message}
            </Form.Error>
          )}
        </div>
        <div className="flex-1" />
      </div>
    )
  }

  const renderRevenueCategoryDefaults = () => {
    return (
      <div
        data-testid="defaults-revenue-category"
        className="flex flex-col sm:gap-6 md:gap-2"
      >
        {renderHeadingRow("Default revenue category", "Revenue Category")}
        {renderRevenueCategoryDefaultRow("dock")}
        {renderRevenueCategoryDefaultRow("mooring")}
      </div>
    )
  }

  const renderDepositDefaults = () => {
    return (
      <div
        data-testid="defaults-deposit-collection"
        className="flex flex-col sm:gap-6 md:gap-2"
      >
        {renderHeadingRow("Default deposit schedule", "Deposit Collection")}
        {renderDepositDefaultRow({
          fieldId: "default-billing-schedule-short-term",
          attributeKey: "short_term_schedule",
          text: "30 nights or shorter",
        })}
        {renderDepositDefaultRow({
          fieldId: "default-billing-schedule-long-term",
          attributeKey: "long_term_schedule",
          text: "longer than 30 nights",
        })}
      </div>
    )
  }

  const renderBalanceColectionDefaults = () => {
    return (
      <div
        data-testid="defaults-balance-collection"
        className="flex flex-col sm:gap-6 md:gap-2"
      >
        {renderHeadingRow(
          "Default remaining balance collection schedule",
          "Balance Collection"
        )}
        {renderBalanceCollectionDefaultRow({
          fieldId: "default-balance-collection-schedule-short-term",
          attributeKey: "short_term_balance_collection_schedule",
          text: "30 nights or shorter",
        })}
        {renderBalanceCollectionDefaultRow({
          fieldId: "default-balance-collection-schedule-long-term",
          attributeKey: "long_term_balance_collection_schedule",
          text: "longer than 30 nights",
        })}
      </div>
    )
  }

  return (
    <div data-testid="defaults" className="flex flex-col gap-4">
      <span className="text-xl font-bold">
        Defaults for transient reservations
      </span>
      {renderRevenueCategoryDefaults()}
      {renderDepositDefaults()}
      {renderBalanceColectionDefaults()}
    </div>
  )
}

TransientDefaults.propTypes = {
  storageProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  reservationInstallmentSchedules: PropTypes.arrayOf(
    PropTypes.shape({
      schedule: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
    })
  ).isRequired,
}

export default TransientDefaults
